import type { ServerClient } from './client';

/**
 * Nachrichten und Steuerbefehle des Servers — `GET /rest/v1/message/pending`,
 * danach `POST /rest/v1/message/confirm`.
 *
 * **Abholen, nicht lauschen.** Relay führt zwar einen Ereignisstrom (`/event/stream`, SSE),
 * der hängt aber am Sitzungscookie des Browsers: `EventSource` kann keine Kopfzeile setzen,
 * und ein Gerät meldet sich mit einem Bearer-Token an. Dazu fehlt im WKWebView von iOS 15
 * `fetch`-Streaming. Für ein Gerät ist der Abholweg deshalb der richtige — er kostet einen
 * Aufruf je Takt und überlebt jeden Funkausfall ohne Sonderbehandlung.
 *
 * Was mit einer Nachricht geschieht, entscheidet der Aufrufer. Hier wird sie geholt, gemeldet
 * und **erst nach der Übergabe** bestätigt: eine Nachricht, die bestätigt ist, kommt nicht
 * wieder, und eine verlorene Anweisung an einen Prüfer ist schlimmer als eine doppelte.
 *
 * ⚠️ **Bestätigt wird nur, was der Aufrufer angenommen hat** (seit dem 2026-10-01). Seit Relay
 * Steuerbefehle schickt (`SET_SETTINGS`), kommen auch Arten an, die Stage noch nicht kennt.
 * Bestätigte die App sie still, wären sie für immer weg — auch für eine spätere Fassung, die
 * sie verstünde. Unbekanntes bleibt deshalb offen, bis Relays Frist (`expiresDate`) es
 * aufräumt.
 */

export interface Nachricht {
  id: string;
  /** Relay unterscheidet Mitteilung und Steuerbefehl; die App zeigt vorerst beides an. */
  type?: string;
  subject?: string;
  message?: string;
  creationDate?: string;
  /** Der Steuerbefehl, etwa `SET_SETTINGS`. Unbekannte Werte überspringt die App still. */
  commandType?: string;
  /** Die Nutzlast des Befehls — je nach Weg als Objekt oder als JSON-Text. */
  commandArgument?: unknown;
  /** Bis wann Relay die Nachricht ausliefert; Abgelaufenes kommt gar nicht erst an. */
  expiresDate?: string;
}

interface PendingResponse {
  messages?: Nachricht[];
  count?: number;
}

export interface NachrichtenDeps {
  client: ServerClient;
  /**
   * Bekommt, was angekommen ist. Wirft sie, wird **nicht** bestätigt.
   *
   * Gibt sie Kennungen zurück, werden nur diese bestätigt; ohne Rückgabe alle.
   */
  uebergeben: (nachrichten: Nachricht[]) => Promise<string[] | void>;
  log: (text: string) => Promise<void>;
  /**
   * Was das Lebenszeichen zusätzlich trägt — `appMode`, `changedAt`, `outboxPending` (Relay,
   * 2026-10-10). Daraus zeigt Relay „aktiv“, „schläft“ oder „nicht erreichbar“. Fehlt es, fragt
   * die App ohne Parameter, was Relay als `ACTIVE` liest.
   */
  zustand?: () => Promise<Record<string, string | number | undefined>>;
}

/**
 * Einmal nachsehen. Gibt zurück, wie viele Nachrichten angenommen und bestätigt wurden.
 */
export async function holeNachrichten(deps: NachrichtenDeps): Promise<number> {
  const zustand = deps.zustand ? await deps.zustand().catch(() => undefined) : undefined;
  const antwort = await deps.client.get<PendingResponse>('/rest/v1/message/pending', zustand);
  const offen = (antwort?.messages ?? []).filter((n) => Boolean(n?.id));
  if (offen.length === 0) return 0;

  const angenommen = (await deps.uebergeben(offen)) ?? offen.map((n) => n.id);
  if (angenommen.length === 0) return 0;

  // Erst jetzt bestätigen. Scheitert das Bestätigen, bekommt die App die Nachricht beim
  // nächsten Takt erneut — doppelt gezeigt ist besser als verloren.
  try {
    await deps.client.post('/rest/v1/message/confirm', { ids: angenommen });
  } catch (fehler) {
    await deps.log(
      'Nachrichten: ' + String(angenommen.length) + ' erhalten, Bestätigen fehlgeschlagen (' +
        String(fehler) + ') — sie kommen erneut.',
    );
    return angenommen.length;
  }

  await deps.log('Nachrichten: ' + String(angenommen.length) + ' erhalten und bestätigt.');
  return angenommen.length;
}
